import { useSourceLanguage } from "../i18n/LocalizedCopy";
// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import { getOrCreateDeviceId } from "../utils/deviceTrust";
import { clearPendingMfaSetup } from "../auth/mfaSetupSession";
import { lockBabysittingOfflineVault } from "../babysitting/babysittingOfflineV9";
import { clearPrivatePwaData, disableServerPushForCurrentDevice } from "../pwa/pwa";
import { clearPublicationDraftsForOwner } from "../unified-journeys/publicationDraftCache";
import {
  readLegacyAuthToken,
  storeLegacyAuthToken,
} from "../security/legacyAuth";
import { MEMBER_AVATAR_UPDATED_EVENT } from "../components/MemberAvatar";

const AuthContext = createContext(null);

const isLocalFrontend =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const API_BASE_URL = isLocalFrontend
  // Local development and production previews both proxy /api to Django.
  // Keep authentication on the page origin, like the feature catalogue.
  ? process.env.REACT_APP_API_BASE_URL_LOCAL || ""
  : process.env.REACT_APP_API_BASE_URL || "";
const AUTH_BOOTSTRAP_TIMEOUT_MS = isLocalFrontend ? 20000 : 4500;

function apiUrl(path) {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path}`;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 5000) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function getStoredToken() {
  return readLegacyAuthToken();
}

function setStoredToken(token) {
  storeLegacyAuthToken(token);
}

function normalizeUser(user) {
  if (!user || typeof user !== "object") return user || null;
  const profile = user.public_profile;
  const hasPublicProfile = profile && typeof profile === "object";
  return {
    ...user,
    display_name: hasPublicProfile
      ? (profile.display_name || user.username || "")
      : (user.display_name || user.username || ""),
    avatar_url: hasPublicProfile
      ? (profile.avatar_display_url || profile.avatar_url || "")
      : (user.avatar_url || ""),
  };
}

function currentAuthHeaders(extra = {}) {
  const token = getStoredToken();
  return {
    ...extra,
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

export function AuthProvider({ children }) {
  useSourceLanguage();
  const [user, setUser] = useState(null); // { id, username, email, profil? }
  const [loading, setLoading] = useState(true);
  const [forumNotifications, setForumNotifications] = useState([]);
  const [forumUnreadCount, setForumUnreadCount] = useState(0);
  const [forumNotificationsNext, setForumNotificationsNext] = useState("");
  const [forumNotifLoading, setForumNotifLoading] = useState(false);
  const [chatConversations, setChatConversations] = useState([]);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [chatNotifLoading, setChatNotifLoading] = useState(false);

  useEffect(() => {
    lockBabysittingOfflineVault();
    return lockBabysittingOfflineVault;
  }, [user?.id]);

  const refreshUser = useCallback(async () => {
    const token = getStoredToken();
    const res = await fetchWithTimeout(apiUrl("/api/auth/me/"), {
      credentials: "include",
      headers: token ? { Authorization: `Token ${token}` } : {},
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);
    if (!res.ok) {
      setUser(null);
      return null;
    }
    const data = await res.json().catch(() => null);
    const nextUser = normalizeUser(data);
    setUser(nextUser);
    return nextUser;
  }, []);

  // Login/MFA responses may omit the public profile returned by /auth/me/.
  useEffect(() => {
    if (!user || user.public_profile) return undefined;
    let cancelled = false;
    const ownerId = user.id;
    const ownerName = user.username;
    fetchWithTimeout(apiUrl("/api/auth/me/"), {
      credentials: "include", headers: currentAuthHeaders(),
    }, AUTH_BOOTSTRAP_TIMEOUT_MS)
      .then((response) => response.ok ? response.json() : null)
      .then((profileUser) => {
        if (cancelled || !profileUser?.public_profile ||
          (ownerId != null && String(profileUser.id) !== String(ownerId))) return;
        setUser((current) => {
          if (!current || current.id !== ownerId || current.username !== ownerName || current.public_profile) return current;
          return normalizeUser({ ...current, public_profile: profileUser.public_profile });
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const updateUserPublicProfile = useCallback((publicProfile) => {
    if (!publicProfile || typeof publicProfile !== "object") return;
    setUser((current) => {
      if (!current) return current;
      const nextProfile = {
        ...(current.public_profile || {}),
        ...publicProfile,
      };
      return normalizeUser({
        ...current,
        public_profile: nextProfile,
      });
    });
  }, []);

  useEffect(() => {
    const onAvatarUpdated = (event) => {
      const detail = event?.detail || {};
      setUser((current) => {
        if (!current || String(current.username || "").toLowerCase() !== String(detail.username || "").toLowerCase()) {
          return current;
        }
        const removed = Boolean(detail.removed);
        return normalizeUser({
          ...current,
          public_profile: {
            ...(current.public_profile || {}),
            avatar_url: removed ? "" : detail.avatar_url || "",
            avatar_variants: removed ? {} : detail.avatar_variants || {},
            avatar_display_url: removed ? "" : detail.avatar_url || "",
            avatar_display_variants: removed ? {} : detail.avatar_variants || {},
          },
        });
      });
    };
    window.addEventListener(MEMBER_AVATAR_UPDATED_EVENT, onAvatarUpdated);
    return () => window.removeEventListener(MEMBER_AVATAR_UPDATED_EVENT, onAvatarUpdated);
  }, []);

  // Au démarrage : on tente de récupérer l'utilisateur connecté via le token
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof window !== "undefined") {
          clearPendingMfaSetup();
        }

        const token = getStoredToken();
        const res = await fetchWithTimeout(apiUrl("/api/auth/me/"), {
          credentials: "include",
          headers: token ? { Authorization: `Token ${token}` } : {},
        }, AUTH_BOOTSTRAP_TIMEOUT_MS);

        if (!res.ok) {
          // token invalide ou expiré
          setStoredToken(null);
          setUser(null);
          setLoading(false);
          return;
        }

        const data = await res.json().catch(() => null);
        setUser(normalizeUser(data));
      } catch (e) {
        console.error("Erreur initAuth:", e);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // 🔐 LOGIN – utilise /api/auth/login/ (CustomAuthToken)
  const login = async (username, password, options = {}) => {
    const deviceId = String(options?.deviceId || getOrCreateDeviceId() || "").trim();
    const rememberDevice = options?.rememberDevice !== false;
    const res = await fetch(apiUrl("/api/auth/login/"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username,
        password,
        device_id: deviceId,
        remember_device: rememberDevice,
      }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      console.error("Réponse non JSON du backend login:", e);
    }

    if (!res.ok) {
      console.error("Status login:", res.status, "body:", data);
      const msg =
        (data && (data.detail || data.error)) ||
        "Identifiants incorrects. Réessaie avec douceur.";
      const err = new Error(msg);
      err.data = data;
      throw err;
    }

    if (data?.mfa_required) {
      return {
        mfaRequired: true,
        mfaTicket: data?.mfa_ticket || "",
        mfaExpiresIn: Number(data?.mfa_expires_in || 0) || 0,
        rememberDeviceRequested: Boolean(data?.remember_device_requested),
      };
    }

    setStoredToken(data?.token || null);

    const userPayload = normalizeUser(data.user ? { ...data.user, profil: data.profil } : data);
    setUser(userPayload);
    return userPayload;
  };

  const verifyMfaLogin = async (mfaTicket, code, options = {}) => {
    const deviceId = String(options?.deviceId || getOrCreateDeviceId() || "").trim();
    const rememberDevice = options?.rememberDevice !== false;
    const res = await fetch(apiUrl("/api/auth/mfa/login/verify/"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mfa_ticket: mfaTicket,
        code,
        device_id: deviceId,
        remember_device: rememberDevice,
      }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (_e) {
      data = null;
    }

    if (!res.ok) {
      const msg =
        (data && (data.detail || data.error)) ||
        "Code Google Authenticator invalide.";
      const err = new Error(msg);
      err.data = data;
      throw err;
    }

    setStoredToken(data?.token || null);
    const userPayload = normalizeUser(data.user ? { ...data.user, profil: data.profil } : data);
    setUser(userPayload);
    return userPayload;
  };

  // 🔏 REGISTER – utilise /api/auth/register/
  const register = async ({
    email,
    password,
    birthDate,
    referralCode = "",
    termsAccepted = false,
    charterAccepted = false,
    pendingIntentId = "",
    policy = {},
  }) => {
    const res = await fetch(apiUrl("/api/auth/register/"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        birth_date: birthDate,
        terms_accepted: !!termsAccepted,
        terms_version: policy.terms_version,
        charter_accepted: !!charterAccepted,
        charter_version: policy.charter_version,
        privacy_version: policy.privacy_version,
        pending_intent_id: String(pendingIntentId || "").trim(),
        referral_code: String(referralCode || "").trim().slice(0, 80),
      }),
    });

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      console.error("Réponse non JSON register:", e);
    }

    if (!res.ok) {
      console.error("Status register:", res.status, "body:", data);
      const msg =
        (data && (data.detail || data.error)) ||
        "Impossible de créer le compte. Vérifie les infos.";
      const err = new Error(msg);
      err.data = data;
      throw err;
    }

    // Make sure no previous session leaks after signup.
    setStoredToken(null);
    setUser(null);

    // Registration must not auto-login the user. The caller continues with
    // e-mail verification; MFA remains an optional setting in the profile.
    return data;
  };

  // 🚪 LOGOUT – supprime le token côté backend (si user) + localStorage
  const logout = async () => {
    lockBabysittingOfflineVault();
    const accountOwnerId = user?.id || user?.username || "";
    try {
      const token = getStoredToken();
      if (token || user) {
        await disableServerPushForCurrentDevice().catch(() => undefined);
        await fetch(apiUrl("/api/auth/logout/"), {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: `Token ${token}` } : {},
        });
      }
    } catch (e) {
      console.error("Erreur logout:", e);
    }
    clearPublicationDraftsForOwner(accountOwnerId);
    try {
      window.sessionStorage.removeItem(`eternua.onboarding.v2.draft.${accountOwnerId}`);
    } catch (_error) {
      // Logout continues when private browser storage is unavailable.
    }
    await clearPrivatePwaData().catch(() => undefined);
    setStoredToken(null);
    setUser(null);
    setForumNotifications([]);
    setForumUnreadCount(0);
    setForumNotificationsNext("");
    setChatConversations([]);
    setChatUnreadCount(0);
  };

  const refreshForumNotifications = useCallback(async () => {
    const token = getStoredToken();
    if (!token && !user) {
      setForumNotifications([]);
      setForumUnreadCount(0);
      setForumNotificationsNext("");
      return [];
    }

    setForumNotifLoading(true);
    try {
      const res = await fetch(apiUrl("/api/forum/notifications/"), {
        credentials: "include",
        headers: currentAuthHeaders(),
      });

      if (!res.ok) {
        return [];
      }

      const data = await res.json().catch(() => null);
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : [];

      const serverUnread = Number(data?.unread_count);
      const unread = Number.isSafeInteger(serverUnread) && serverUnread >= 0
        ? serverUnread
        : items.reduce(
          (acc, item) => (item?.est_lu ? acc : acc + 1),
          0
        );
      setForumNotifications(items);
      setForumUnreadCount(unread);
      setForumNotificationsNext(
        typeof data?.next === "string" ? data.next : ""
      );
      return items;
    } catch (e) {
      console.error("Erreur refresh notifications forum:", e);
      return [];
    } finally {
      setForumNotifLoading(false);
    }
  }, [user]);

  const loadMoreForumNotifications = useCallback(async () => {
    const next = String(forumNotificationsNext || "").trim();
    const token = getStoredToken();
    if (!next || forumNotifLoading || (!token && !user)) return [];

    let requestedUrl;
    try {
      const expected = new URL(
        apiUrl("/api/forum/notifications/"),
        window.location.origin
      );
      const candidate = new URL(next, expected);
      if (
        candidate.origin !== expected.origin
        || candidate.pathname !== expected.pathname
      ) {
        return [];
      }
      requestedUrl = candidate.toString();
    } catch (_error) {
      return [];
    }

    setForumNotifLoading(true);
    try {
      const response = await fetch(requestedUrl, {
        credentials: "include",
        headers: currentAuthHeaders(),
      });
      if (!response.ok) return [];
      const data = await response.json().catch(() => null);
      const items = Array.isArray(data?.results) ? data.results : [];
      setForumNotifications((current) => {
        const knownIds = new Set(current.map((item) => String(item?.id || "")));
        return [
          ...current,
          ...items.filter((item) => !knownIds.has(String(item?.id || ""))),
        ];
      });
      setForumNotificationsNext(
        typeof data?.next === "string" ? data.next : ""
      );
      const serverUnread = Number(data?.unread_count);
      if (Number.isSafeInteger(serverUnread) && serverUnread >= 0) {
        setForumUnreadCount(serverUnread);
      }
      return items;
    } catch (error) {
      console.error("Erreur pagination notifications forum:", error);
      return [];
    } finally {
      setForumNotifLoading(false);
    }
  }, [forumNotifLoading, forumNotificationsNext, user]);

  const markForumNotificationRead = useCallback(async (notifId) => {
    if (!notifId) return;
    const token = getStoredToken();
    if (!token && !user) return;

    try {
      await fetch(apiUrl(`/api/forum/notifications/${notifId}/`), {
        method: "PATCH",
        credentials: "include",
        headers: currentAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ est_lu: true }),
      });
    } catch (e) {
      console.error("Erreur mark notification lue:", e);
    }

    setForumNotifications((prev) =>
      prev.map((item) =>
        item.id === notifId ? { ...item, est_lu: true } : item
      )
    );
    setForumUnreadCount((prev) => Math.max(0, prev - 1));
  }, [user]);

  const clearForumNotifications = useCallback(async () => {
    const token = getStoredToken();
    if (!token && !user) return;

    try {
      await fetch(apiUrl("/api/forum/clear-notifications/"), {
        method: "POST",
        credentials: "include",
        headers: currentAuthHeaders(),
      });
    } catch (e) {
      console.error("Erreur clear notifications forum:", e);
    }

    setForumNotifications((prev) =>
      prev.map((item) => ({ ...item, est_lu: true }))
    );
    setForumUnreadCount(0);
  }, [user]);

  const refreshChatNotifications = useCallback(async () => {
    const token = getStoredToken();
    if (!token && !user) {
      setChatConversations([]);
      setChatUnreadCount(0);
      return [];
    }

    setChatNotifLoading(true);
    try {
      const res = await fetch(apiUrl("/api/chat/conversations/?limit=40"), {
        credentials: "include",
        headers: currentAuthHeaders(),
      });
      if (!res.ok) {
        return [];
      }
      const data = await res.json().catch(() => null);
      const items = Array.isArray(data) ? data : [];
      const unread = items.reduce(
        (acc, item) => acc + (Number(item?.unread_count || 0) || 0),
        0
      );
      setChatConversations(items);
      setChatUnreadCount(unread);
      return items;
    } catch (e) {
      console.error("Erreur refresh notifications chat:", e);
      return [];
    } finally {
      setChatNotifLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setForumNotifications([]);
      setForumUnreadCount(0);
      setForumNotificationsNext("");
      setChatConversations([]);
      setChatUnreadCount(0);
      return;
    }

    refreshForumNotifications();
    refreshChatNotifications();
    const interval = window.setInterval(() => {
      refreshForumNotifications();
      refreshChatNotifications();
    }, 20000);
    return () => window.clearInterval(interval);
  }, [user, refreshForumNotifications, refreshChatNotifications]);

  useEffect(() => {
    if (!user) return undefined;
    const onChatChanged = () => {
      refreshChatNotifications();
    };
    window.addEventListener("eternua:chat-updated", onChatChanged);
    return () => {
      window.removeEventListener("eternua:chat-updated", onChatChanged);
    };
  }, [user, refreshChatNotifications]);

  const totalUnreadCount = useMemo(
    () => (Number(forumUnreadCount || 0) || 0) + (Number(chatUnreadCount || 0) || 0),
    [forumUnreadCount, chatUnreadCount]
  );

  const value = {
    user,
    loading,
    login,
    verifyMfaLogin,
    register,
    refreshUser,
    updateUserPublicProfile,
    logout,
    forumNotifications,
    forumUnreadCount,
    forumNotifLoading,
    refreshForumNotifications,
    loadMoreForumNotifications,
    forumNotificationsHasMore: Boolean(forumNotificationsNext),
    markForumNotificationRead,
    clearForumNotifications,
    chatConversations,
    chatUnreadCount,
    chatNotifLoading,
    refreshChatNotifications,
    totalUnreadCount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return ctx;
}

// Public member controls are also rendered in isolated previews where the
// application provider can intentionally be absent.
export function useOptionalAuth() {
  return useContext(AuthContext);
}
